import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useParams, Link } from "react-router-dom";
import { authFetch } from './auth';

const smoothData = (data, windowSize = 5) => {
  return data.map((point, index, array) => {
    let start = Math.max(0, index - Math.floor(windowSize / 2));
    let end = Math.min(array.length - 1, index + Math.floor(windowSize / 2));
    let values = array.slice(start, end + 1).map((p) => p.speed);
    let avgSpeed = Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
    return {
      ...point,
      speed: Math.ceil(avgSpeed),
      altitude: Math.ceil(point.altitude),
      time: Math.round(point.time),
    };
  });
};
const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
// Форматируем время из секунд в "X мин Y сек"
const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined) return "-";
  if (seconds < 60) return `${seconds} сек`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs === 0 ? `${mins} мин` : `${mins} мин ${secs} сек`;
};

// Выводим поля, если данных нет — "-"
const displayField = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const App = () => {
  const { id } = useParams();
  const [jump, setJump] = useState({});
  const [kmhData, setKmhData] = useState([]);
  const [msData, setMsData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [speedUnit, setSpeedUnit] = useState("kmh");
  const [maxSpeedKmH, setMaxSpeedKmH] = useState(0);
  const [maxSpeedMS, setMaxSpeedMS] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const publicUrl = jump.publicId
    ? `${window.location.origin}/jump/${jump.publicId}`
    : "";

  useEffect(() => {
  if (!localStorage.getItem("token")) return;

  authFetch("/api/jumpdata/" + id)
    .then((response) => response.json())
    .then((data) => {
      setJump(data);

      const altDataRaw = Array.isArray(data.altimeterData) ? data.altimeterData : [];

      // 🔥 обрезаем на первом altitude === 0
      let cutIndex = altDataRaw.findIndex(point => point.altitude === 0);

      // если ноль не найден — берем весь массив
      if (cutIndex === -1) {
        cutIndex = altDataRaw.length;
      }

      const altData = altDataRaw.slice(0, cutIndex);

      // Обрабатываем данные для графика
      if (altData.length > 0) {
        const processedData = altData.map((point) => ({
          time: Math.round(point.timeOfJump),
          altitude: Math.ceil(point.altitude),
          speed: Math.ceil(point.speed),
          duration: Math.round(point.duration),
        }));

        const smoothedData = smoothData(processedData);

        setKmhData(smoothedData);
        setMsData(smoothedData.map(p => ({ ...p, speed: Math.round(p.speed / 3.6) })));

        const maxS = Math.ceil(Math.max(...altData.map(d => d.speed)));
        setMaxSpeedKmH(maxS);
        setMaxSpeedMS(Math.round(maxS / 3.6));
      } else {
        setKmhData([]);
        setMsData([]);
        setMaxSpeedKmH(0);
        setMaxSpeedMS(0);
      }
    });
}, [id]);

  const modalOverlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  };

  const menuItemStyle = {
    padding: "10px",
    cursor: "pointer",
    color: "#F3F4F6",
    borderRadius: "8px",
    transition: "background 0.2s",
    textAlign: "left",
  };
  const modalTitleStyle = {
    fontSize: "22px",
    marginBottom: "20px",
  };
  const inputStyle = {
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.2)",
    backgroundColor: "rgba(31, 41, 55, 0.9)",
    color: "#F3F4F6",
    outline: "none",
  };
  const cancelBtnStyle = {
    flex: 1,
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.2)",
    backgroundColor: "transparent",
    color: "#F3F4F6",
    cursor: "pointer",
  };
  const modalStyle = {
    backgroundColor: "rgba(17, 24, 39, 0.95)",
    borderRadius: "16px",
    padding: "30px",
    width: "100%",
    maxWidth: "420px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
    color: "#F3F4F6",
  };
  const handleUnitChange = (newUnit) => {
    setSpeedUnit(newUnit);
    setDisplayData(newUnit === "kmh" ? maxSpeedKmH : maxSpeedMS);
  };

  const handleCopyLink = async () => {
    try {
      // Если браузер поддерживает Clipboard API и страница в безопасном контексте
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(publicUrl);
      } else {
        // Fallback для небезопасного контекста (HTTP) или старых браузеров
        const textArea = document.createElement("textarea");
        textArea.value = publicUrl;
        textArea.style.position = "fixed"; // чтобы не скроллилось
        textArea.style.left = "-9999px"; // чтобы было вне видимой области
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (!successful) throw new Error("Fallback copy failed");
      }

      // Если удалось скопировать
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

    } catch (err) {
      alert("Не удалось скопировать ссылку");
      console.error(err);
    }
  };



  const handleDelete = async () => {
    try {
      const response = await authFetch("/api/jumpdata/" + id, { method: "DELETE" });
      if (!response.ok) throw new Error("Ошибка удаления");
      alert("Прыжок успешно удалён");
      window.location.href = "/jumps";
    } catch (error) {
      alert("Не удалось удалить прыжок");
      console.error(error);
    }
  };

  // Выбираем массив для графика в зависимости от единиц
  const chartData = speedUnit === "kmh" ? kmhData : msData;
  const chartMaxSpeed = speedUnit === "kmh" ? maxSpeedKmH : maxSpeedMS;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "20px",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <div
        style={{
          width: "95%",
          maxWidth: "1200px",
          backgroundColor: "rgba(17, 17, 17, 0.75)",
          borderRadius: "20px",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.3)",
          padding: "30px",
          position: "relative",
        }}
      >
        {/* Три точки */}
        <div style={{ position: "absolute", top: "20px", right: "20px" }}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: "22px",
              cursor: "pointer",
            }}
          >
            ⋮
          </button>

          {menuOpen && (
            <div
              style={{
                position: "absolute",
                right: 0,
                marginTop: "10px",
                background: "#111",
                borderRadius: "10px",
                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                padding: "10px",
                minWidth: "140px",
                zIndex: 1000,
              }}
            >
              <div
                style={menuItemStyle}
                onClick={() => {
                  setShareModalOpen(true);
                  setMenuOpen(false);
                }}
              >
                🔗 Поделиться
              </div>

              <div
                style={{
                  padding: "8px",
                  cursor: "pointer",
                  color: "#DC2626",
                  fontWeight: "bold",
                }}
                onClick={() => {
                  setMenuOpen(false);
                  setConfirmOpen(true);
                }}
              >
                🗑 Удалить
              </div>
            </div>
          )}
          {shareModalOpen && (
            <div
              style={modalOverlayStyle}
              onClick={() => {
                setShareModalOpen(false);
                setCopied(false);
              }}
            >
              <div
                style={modalStyle}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={modalTitleStyle}>Поделиться прыжком</h3>

                <input
                  type="text"
                  value={publicUrl}
                  readOnly
                  style={{
                    ...inputStyle,
                    marginBottom: "15px",
                    cursor: "pointer"
                  }}
                  onClick={(e) => e.target.select()}
                />

                <button
                  onClick={handleCopyLink}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: "#10B981",
                    color: "white",
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                >
                  {copied ? "✅ Скопировано!" : "📋 Скопировать"}
                </button>

                <button
                  onClick={() => {
                    setShareModalOpen(false);
                    setCopied(false);
                  }}
                  style={{
                    ...cancelBtnStyle,
                    marginTop: "12px",
                    width: "100%"
                  }}
                >
                  Закрыть
                </button>
              </div>
            </div>
          )}
        </div>

        <h2 style={{ textAlign: "center", marginBottom: "20px", color: "#fff" }}>
          Анализ прыжка с парашютом
        </h2>

        {/* График или плашка при отсутствии данных */}
        {chartData.length === 0 ? (
          <div
            style={{
              margin: "20px auto",
              background: "rgba(17,17,17,0.7)",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid rgba(255,255,255,0.2)",
              width: "100%",
              maxWidth: "900px",
              color: "#fff",
              textAlign: "center",
              overflowWrap: "break-word",
              boxSizing: "border-box",
            }}
          >
            Данный прыжок добавлен ручным способом, показания прибора для построения графика прыжка отсутствуют
          </div>
        ) : (
          <>
            <ResponsiveContainer width="95%" height={400}>
              <LineChart data={chartData}>
                <XAxis
                  dataKey="time"
                  label={{ value: "Время (сек)", position: "insideBottom", dy: 10 }}
                  stroke="#fff"
                />
                <YAxis
                  yAxisId="left"
                  label={{ value: "Высота (м)", angle: -90, position: "insideLeft" }}
                  stroke="#fff"
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{ value: `Скорость (${speedUnit === "kmh" ? "км/ч" : "м/с"})`, angle: -90, position: "insideRight" }}
                  domain={[0, chartMaxSpeed]}
                  stroke="#fff"
                />
                <Tooltip
                  contentStyle={{ backgroundColor: "#222", color: "#fff", border: "none" }}
                  itemStyle={{ color: "#fff" }}
                  labelStyle={{ color: "#fff" }}
                />
                <Legend wrapperStyle={{ marginTop: 30, color: "#fff" }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="altitude"
                  stroke="#5372c7"
                  strokeWidth={4}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="natural"
                  dataKey="speed"
                  stroke="#DC2626"
                  strokeWidth={3}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Переключатель единиц */}
            {/* <div style={{ marginTop: "10px", textAlign: "center" }}>
              <button
                onClick={() => setSpeedUnit(speedUnit === "kmh" ? "ms" : "kmh")}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#FFD700",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {speedUnit === "kmh" ? "Показать м/с" : "Показать км/ч"}
              </button>
            </div> */}

            <div style={{ marginTop: "15px", display: "flex", justifyContent: "center", gap: "10px" }}>
              <button
                onClick={() => handleUnitChange("kmh")}
                style={{
                  padding: "6px 12px", borderRadius: "8px",
                  border: speedUnit === "kmh" ? "2px solid #FFD700" : "none",
                  background: "#333", color: "#fff", cursor: "pointer"
                }}>км/ч</button>
              <button
                onClick={() => handleUnitChange("ms")}
                style={{
                  padding: "6px 12px", borderRadius: "8px",
                  border: speedUnit === "ms" ? "2px solid #FFD700" : "none",
                  background: "#333", color: "#fff", cursor: "pointer"
                }}>м/с</button>
            </div>
          </>
        )}

        {/* Информация о прыжке - логические блоки */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            marginTop: "20px",
            gap: "15px",
          }}
        >
          {/* Локация */}
          <div style={{ flex: "1 1 250px", background: "rgba(17,17,17,0.6)", padding: "15px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
            <p><strong>Дропзона:</strong> {displayField(jump.dropzone)}</p>
            <p><strong>Страна:</strong> {displayField(jump.country)}</p>
            <p><strong>Город:</strong> {displayField(jump.city)}</p>
          </div>

          {/* Прыжок и аппарат */}
          <div style={{ flex: "1 1 250px", background: "rgba(17,17,17,0.6)", padding: "15px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
            <p><strong>Номер прыжка:</strong> {displayField(jump.jumpNumber)}</p>
            <p><strong>Дата и время:</strong> {formatDateTime(jump.dateTime)}</p>
            <p><strong>Тип прыжка:</strong> {displayField(jump.jumpType)}</p>
            <p><strong>Тип летательного аппарата:</strong> {displayField(jump.aircraftType)}</p>
            <p><strong>Тип купола:</strong> {displayField(jump.canopyType)}</p>
          </div>

          {/* Высоты и скорости */}
          <div style={{ flex: "1 1 250px", background: "rgba(17,17,17,0.6)", padding: "15px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
            <p><strong>Высота отделения:</strong> {displayField(jump.exitAltitude)}</p>
            <p><strong>Высота раскрытия:</strong> {displayField(jump.deployAltitude)}</p>
            <p><strong>Максимальная скорость:</strong> {chartData.length === 0 ? "-" : (speedUnit === "kmh" ? maxSpeedKmH + " км/ч" : maxSpeedMS + " м/с")}</p>
            <p><strong>Время свободного падения:</strong> {formatTime(jump.freefallTime)}</p>
            <p><strong>Время под куполом:</strong> {formatTime(jump.canopyPilotTime)}</p>
            <p><strong>Общая длительность прыжка:</strong> {formatTime(jump.jumpDuration)}</p>
          </div>
        </div>

        {/* Комментарий */}
        <div style={{
          margin: "20px auto",
          background: "rgba(17,17,17,0.7)",
          padding: "20px",
          borderRadius: "12px",
          border: "1px solid rgba(255,255,255,0.2)",
          width: "100%",
          maxWidth: "900px",
          color: "#fff",
          textAlign: "center",
          overflowWrap: "break-word",
          boxSizing: "border-box",
        }}>
          <h3 style={{ color: "#FFD700", marginBottom: "10px" }}>Описание прыжка</h3>
          <p style={{ whiteSpace: "pre-wrap", textAlign: "center", margin: 0 }}>
            {displayField(jump.comment)}
          </p>
        </div>

        {/* Кнопки навигации */}
        <div
          style={{
            marginTop: "20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <Link
            to="/jumps"
            style={{
              padding: "10px 20px",
              backgroundColor: "#FFD700",
              color: "#000",
              textDecoration: "none",
              borderRadius: "8px",
              fontWeight: "bold",
            }}
          >
            ← Назад к списку прыжков
          </Link>

          <Link
            to="/"
            style={{
              padding: "10px 20px",
              backgroundColor: "#FFD700",
              color: "#000",
              textDecoration: "none",
              borderRadius: "8px",
              fontWeight: "bold",
            }}
          >
            ← На главную
          </Link>
        </div>
      </div>

      {/* Модальное окно удаления */}
      {confirmOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              background: "#111",
              padding: "30px",
              borderRadius: "15px",
              width: "350px",
              textAlign: "center",
              boxShadow: "0 10px 25px rgba(0,0,0,0.8)",
            }}
          >
            <h3 style={{ color: "#fff" }}>Удалить прыжок?</h3>
            <p style={{ color: "#aaa", marginTop: "10px" }}>
              Это действие нельзя отменить.
            </p>

            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <button
                onClick={() => setConfirmOpen(false)}
                style={{
                  padding: "8px 15px",
                  background: "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Отмена
              </button>

              <button
                onClick={handleDelete}
                style={{
                  padding: "8px 15px",
                  background: "#DC2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;